'use client';

import React, { ReactNode, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styles from './tooltip.module.sass';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

export interface TooltipProps {
  content: string | ReactNode;
  children: ReactNode;
  position?: TooltipPosition;
  className?: string;
}

type BubbleCoords = { top: number; left: number; ready: boolean };

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  position = 'top',
  className,
}) => {
  const [coords, setCoords] = useState<BubbleCoords | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);

  const show = useCallback(() => {
    if (!triggerRef.current) return;
    // Render off-screen first so we can measure the bubble
    setCoords({ top: -9999, left: -9999, ready: false });
  }, []);

  const hide = useCallback(() => setCoords(null), []);

  // Called by the bubble's ref callback — fires once bubble is in the DOM
  const measureAndPlace = useCallback((el: HTMLDivElement | null) => {
    (bubbleRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    if (!el || !triggerRef.current) return;
    const t = triggerRef.current.getBoundingClientRect();
    const b = el.getBoundingClientRect();
    const GAP = 8;
    let top = 0;
    let left = 0;
    if (position === 'top') {
      top  = t.top - b.height - GAP;
      left = t.left + t.width / 2 - b.width / 2;
    } else if (position === 'bottom') {
      top  = t.bottom + GAP;
      left = t.left + t.width / 2 - b.width / 2;
    } else if (position === 'left') {
      top  = t.top + t.height / 2 - b.height / 2;
      left = t.left - b.width - GAP;
    } else {
      top  = t.top + t.height / 2 - b.height / 2;
      left = t.right + GAP;
    }
    const MARGIN = 8;
    left = Math.max(MARGIN, Math.min(left, window.innerWidth  - b.width  - MARGIN));
    top  = Math.max(MARGIN, Math.min(top,  window.innerHeight - b.height - MARGIN));
    setCoords({ top, left, ready: true });
  }, [position]);

  const bubble = coords && createPortal(
    <div
      ref={measureAndPlace}
      className={`${styles['tooltip-bubble']} ${coords.ready ? styles['tooltip-visible'] : ''} ${styles[`tooltip-pos-${position}`]}`}
      role="tooltip"
      style={{ position: 'fixed', top: coords.top, left: coords.left, transform: 'none', margin: 0 }}
    >
      {content}
      <div className={styles['tooltip-arrow']} />
    </div>,
    document.body,
  );

  return (
    <span
      ref={triggerRef}
      className={`${styles['tooltip-wrapper']} ${className || ''}`}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      {children}
      {bubble}
    </span>
  );
};

export default Tooltip;
